/**
 * ScrollButtons Module
 * Floating scroll-to-top and scroll-to-bottom navigation buttons
 *
 * Features:
 * - Shows scroll-to-top when scrolled down > 200px
 * - Shows scroll-to-bottom when not at bottom (100px threshold)
 * - Semi-transparent cyberpunk styling
 * - Smooth scroll behavior
 *
 * Created: 2025-12-26
 * 100% standalone - no external dependencies except React
 */

import { useState, useEffect } from 'react'
import './ScrollButtons.css'

function ScrollButtons() {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [showScrollBottom, setShowScrollBottom] = useState(false)

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = window.innerHeight

      // Show top button when scrolled down more than 200px
      setShowScrollTop(scrollTop > 200)

      // Show bottom button when not at the bottom (with 100px threshold)
      setShowScrollBottom(scrollTop + clientHeight < scrollHeight - 100)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }

  // Don't render if both buttons hidden
  if (!showScrollTop && !showScrollBottom) {
    return null
  }

  return (
    <div className="sb-container">
      {showScrollTop && (
        <button
          className="sb-btn sb-top"
          onClick={scrollToTop}
          title="Scroll to top"
          aria-label="Scroll to top"
        >
          <i className="fas fa-chevron-up"></i>
        </button>
      )}
      {showScrollBottom && (
        <button
          className="sb-btn sb-bottom"
          onClick={scrollToBottom}
          title="Scroll to bottom"
          aria-label="Scroll to bottom"
        >
          <i className="fas fa-chevron-down"></i>
        </button>
      )}
    </div>
  )
}

export default ScrollButtons
